import { Alerts } from '@/feature/alerts/components/AlertsProvider';
import { EntryErrorType } from '@/types/api.generated';
import { logger } from '@/utils/logging';
import { runStoreUpdate } from '@/utils/zustand';
import i18n from 'i18next';

import { PicksValidationProcessor, ValidateResponse } from '.';
import { RestrictedCombo } from '../components/RestrictedCombinationModalProvider';
import { useBetslipStore } from '../hooks/use-betslip-store';
import { BetslipPick, PlayerWithTeam } from '../types';
import {
    containsRestrictionErrors,
    containsSingleRestrictionError,
    getRestrictedCombinationErrorMsg,
} from '../utils/betslip-utils';

export type PicksRestrictionProcessorContext = {
    latestPick: BetslipPick | undefined;
    alertsContext: Alerts;
    restrictedComboAlertContext: RestrictedCombo;
    removeFunction: (picks: { eventId: string; player: PlayerWithTeam }[]) => void;
};

const LOG_TAG = '[Betslip-Restrictions]';

export class PicksRestrictionsProcessor extends PicksValidationProcessor<PicksRestrictionProcessorContext> {
    process(data: ValidateResponse): void {
        const activeGameMode = useBetslipStore.getState().activeGameMode;
        const picks = useBetslipStore.getState().betslip;
        const result = data.validateEntryOnEntryRules.find(it => it.gameMode === activeGameMode);

        if (!result) {
            logger.warn(LOG_TAG, `No validation result for active ${activeGameMode} game mode found.`);
            return;
        }

        if (!containsRestrictionErrors(result)) {
            //If there are no restriction errors, proceed to the next processor
            this.nextProcessor?.process(data);
            return;
        }

        const restrictionErrors = result.errors.filter(it => it.type === EntryErrorType.Restriction);
        const description = getRestrictedCombinationErrorMsg(restrictionErrors);

        const removeLastPickAndShowRestrictionError = () => {
            runStoreUpdate(() => {
                if (this.context.latestPick) {
                    this.context.removeFunction([this.context.latestPick]);
                    this.context.alertsContext.clearAllToasts();
                    this.context.alertsContext.showInfoSheet({
                        errorStyling: true,
                        title: i18n.t('error:betslip_pickem.restricted_combination'),
                        buttonLabel: 'Ok',
                        description,
                    });
                }
            });
            logger.warn(
                LOG_TAG,
                'Restricted combination error with multiple restrictions!? Falling back to old stilling.',
                result.errors
            );
        };

        if (containsSingleRestrictionError(restrictionErrors[0])) {
            const restrictedIds = restrictionErrors[0]?.restrictedPlayerIdPairs?.[0] ?? [];
            if (restrictedIds.length >= 2) {
                //Sometimes for some restrictions there are more than 2 players in the 'pair'.(eg: for PGA)
                //So we are ensuring we are always showing the latestPick in the modal
                //and the first player in the restrictedPlayers array that comes from the BE
                const restrictedPlayers = picks
                    //find players that are restricted
                    .filter(pick => restrictedIds.includes(pick.player.id))
                    //exclude the latest pick
                    .filter(pick => pick.player.id !== this.context.latestPick?.player.id)
                    //reverse so we show the restriction against the latest pick
                    .reverse();
                const [player1, player2] = [this.context.latestPick, ...restrictedPlayers].filter(Boolean);
                if (player1 && player2) {
                    this.context.alertsContext.clearAllToasts();
                    this.context.restrictedComboAlertContext.showRestrictedComboModal({
                        errorCode: restrictionErrors[0].code,
                        restrictedCombo: [player2, player1],
                        removePlayers: this.context.removeFunction,
                        message: restrictionErrors[0].message,
                    });
                } else {
                    logger.warn(
                        LOG_TAG,
                        'Restricted combination error, but players not found in betslip.',
                        result.errors
                    );
                }
            } else {
                removeLastPickAndShowRestrictionError();
            }
        } else {
            removeLastPickAndShowRestrictionError();
        }
    }
}
